/**
 * Which size of a person's camera each receiver gets. The camera is sent in three sizes (liveMedia.ts); the SFU
 * sends a receiver the largest its bandwidth carries, and the kit never tells it how big a tile is, because
 * this page draws its own video elements and gives the kit none. So a 112 pixel tile was sent 1080p.
 *
 * The kit has one way to ask for less: a video element registered on a participant (`registerVideoElement`)
 * makes it send the smallest size when the element is much smaller than the picture, and a larger one when it
 * is much bigger. It reads the size of the element it was given, once two seconds pass without a change
 * (checked in @cloudflare/realtimekit 2.0.3). Its larger one is the half size, so vite-kit-full-size.ts
 * rewrites that to the full size at build time. The choice made here is then two-way for everyone, the host
 * included: a small tile gets the smallest size, any bigger tile gets the full size, and a person can go back
 * and forth. The element is a hidden stand-in we size ourselves, so the answer depends on the tile and not on
 * the ratio the kit would work out against the picture it is receiving.
 */

/** The smallest size sent is 480 wide; a tile up to 640 screen pixels wide shows it with at most a third of stretch. */
export const SMALL_PIXELS = 640;

/** Whether the biggest tile this person is shown in is small, in screen pixels. Nobody shown anywhere is not small. */
export function wantsSmall(widths: Iterable<number>, pixelRatio = 1): boolean {
  let widest = -1;
  for (const w of widths) widest = Math.max(widest, w);
  return widest >= 0 && widest * pixelRatio <= SMALL_PIXELS;
}

/** How wide the stand-in is told it is: much smaller than any picture, and much bigger than any. */
const TINY = 8;
const HUGE = 4000;
/** The kit decides two seconds after the last change; a second change within that would hide the first. */
const KIT_QUIET_MS = 2200;

/** The two things of a person this needs from the kit. */
interface Watchable {
  videoTrack?: MediaStreamTrack | null;
  registerVideoElement(el: HTMLVideoElement): void;
  deregisterVideoElement(el?: HTMLVideoElement): void;
}

interface Watched {
  el: HTMLVideoElement;
  /** The picture the stand-in was last sized for, so a new picture (a camera turned off and on) asks again. */
  trackId: string | null;
  small: boolean;
  /** A pending second step of asking again. */
  timer: number | undefined;
}

/** A stand-in the kit watches in place of a tile: invisible, and as wide as it is told. */
function standIn(): HTMLVideoElement {
  const el = document.createElement("video");
  el.muted = true;
  el.playsInline = true;
  el.setAttribute("aria-hidden", "true");
  el.style.cssText = "position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none;";
  el.style.width = `${HUGE}px`;
  el.style.height = `${HUGE}px`;
  document.body.appendChild(el);
  return el;
}

/** The sizes of every tile showing a person, and the stand-ins the kit is made to watch. */
export class TileLayers {
  private readonly sizes = new Map<string, Map<string, number>>();
  private readonly watched = new Map<string, Watched>();

  constructor(
    private readonly person: (id: string) => Watchable | undefined,
    private readonly make: () => HTMLVideoElement = standIn,
    private readonly pixelRatio: () => number = () => window.devicePixelRatio || 1,
  ) {}

  /** A tile (`key`) showing this person is `width` wide now, or gone when `width` is null. */
  seen(id: string, key: string, width: number | null): void {
    const tiles = this.sizes.get(id) ?? new Map<string, number>();
    if (width === null) tiles.delete(key);
    else tiles.set(key, width);
    if (tiles.size === 0) this.sizes.delete(id);
    else this.sizes.set(id, tiles);
    this.apply(id);
  }

  /** The kit has news (a picture started or changed): ask again where a person's picture is new. */
  refresh(): void {
    for (const [id, w] of this.watched) {
      const track = this.person(id)?.videoTrack?.id ?? null;
      if (w.small && track !== w.trackId) this.apply(id, { reset: w.trackId !== null });
    }
  }

  /** The kit's people are new (the meeting was joined again): watch them again as their tiles already show. */
  rebind(): void {
    for (const id of Array.from(this.watched.keys())) this.drop(id);
    for (const id of this.sizes.keys()) this.apply(id);
  }

  /** Let go of everyone: the page is leaving. */
  clear(): void {
    this.sizes.clear();
    for (const id of Array.from(this.watched.keys())) this.drop(id);
  }

  /** `again` is set when a new picture needs the size asked for again; `reset` when the kit may think it has it already. */
  private apply(id: string, again?: { reset: boolean }): void {
    const small = wantsSmall(this.sizes.get(id)?.values() ?? [], this.pixelRatio());
    const had = this.watched.get(id);
    if (!had && !small) return;
    const person = this.person(id);
    if (!person) {
      if (had) this.drop(id);
      return;
    }
    const w = had ?? this.watch(id, person);
    if (!again && w.small === small) return;
    w.small = small;
    w.trackId = person.videoTrack?.id ?? null;
    window.clearTimeout(w.timer);
    w.timer = undefined;
    if (!small) {
      this.size(w.el, HUGE);
    } else if (again?.reset) {
      // The kit thinks the person is already at the small size, so it is first told they are not.
      this.size(w.el, HUGE);
      w.timer = window.setTimeout(() => this.size(w.el, TINY), KIT_QUIET_MS);
    } else {
      this.size(w.el, TINY);
    }
  }

  private watch(id: string, person: Watchable): Watched {
    const el = this.make();
    const w: Watched = { el, trackId: null, small: false, timer: undefined };
    this.watched.set(id, w);
    person.registerVideoElement(el);
    return w;
  }

  private drop(id: string): void {
    const w = this.watched.get(id);
    if (!w) return;
    window.clearTimeout(w.timer);
    this.watched.delete(id);
    try {
      this.person(id)?.deregisterVideoElement(w.el);
    } finally {
      w.el.remove();
    }
  }

  private size(el: HTMLVideoElement, px: number): void {
    el.style.width = `${px}px`;
    el.style.height = `${px}px`;
  }
}
